'use client'

import { useMemo } from 'react'

import _ from 'lodash'

import { FlowState } from '@/shared/lib/steps-flow/types'

interface Alternative {
  name: string
  is_correct?: boolean
}

interface QuestionMultipleChoiceProps {
  alternatives: Alternative[]
  shuffle?: boolean
  selectedAnswer: string | string[] | null
  onSelect: (answer: string | string[]) => void
  disabled: boolean
  state: FlowState
}

export function QuestionMultipleChoice({
  alternatives,
  shuffle,
  selectedAnswer,
  onSelect,
  disabled,
  state
}: QuestionMultipleChoiceProps) {
  const processedAlternatives = useMemo<Alternative[]>(() => {
    let processed = alternatives
    processed = shuffle ? _.shuffle(processed) : processed
    processed = _.uniqBy(processed, 'name')
    return processed
  }, [alternatives, shuffle])

  const correctLength = processedAlternatives.filter(
    (alt: Alternative) => alt.is_correct === true
  ).length
  const isMultiple = correctLength >= 2
  const showSelect = processedAlternatives.length > 8
  const hasNoAlternatives = processedAlternatives.length === 0

  const handleSelect = (name: string) => {
    if (isMultiple) {
      const newAnswer =
        Array.isArray(selectedAnswer) && selectedAnswer.includes(name)
          ? selectedAnswer.filter((opt: string) => opt !== name)
          : [...(Array.isArray(selectedAnswer) ? selectedAnswer : []), name]
      onSelect(newAnswer)
    } else {
      onSelect(name)
    }
  }

  const checkIsSelected = (name: string) => {
    if (isMultiple) {
      return Array.isArray(selectedAnswer) && selectedAnswer.includes(name)
    }
    return selectedAnswer === name
  }

  const getButtonColor = (isSelected: boolean) => {
    if (state.phase === 'CORRECT' && isSelected) {
      return 'bg-green-500 text-white'
    } else if (state.phase === 'LOCKED_WRONG' && isSelected) {
      return 'bg-red-500 text-white'
    } else if (isSelected) {
      return 'bg-primary text-white ring-2 ring-primary'
    }
    return 'bg-gray-100 text-gray-800 hover:bg-gray-200'
  }

  if (hasNoAlternatives) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center text-sm font-medium text-amber-800">
        Esta questão está sem alternativas cadastradas. Você pode avançar para
        continuar a jornada.
      </div>
    )
  }

  if (showSelect) {
    if (isMultiple) {
      return (
        <select
          multiple
          disabled={disabled}
          value={Array.isArray(selectedAnswer) ? selectedAnswer : []}
          onChange={e => {
            const selected = Array.from(e.target.selectedOptions).map(
              o => o.value
            )
            onSelect(selected)
          }}
          className="w-full rounded-lg border border-gray-300 bg-white p-2"
        >
          {processedAlternatives.map((opt: Alternative) => (
            <option key={opt.name} value={opt.name}>
              {opt.name}
            </option>
          ))}
        </select>
      )
    }

    return (
      <select
        disabled={disabled}
        value={typeof selectedAnswer === 'string' ? selectedAnswer : ''}
        onChange={e => onSelect(e.target.value)}
        className="w-full rounded-lg border border-gray-300 bg-white p-2"
      >
        <option value="">Selecione uma opção</option>
        {processedAlternatives.map((opt: Alternative) => (
          <option key={opt.name} value={opt.name}>
            {opt.name}
          </option>
        ))}
      </select>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {processedAlternatives.map((opt: Alternative, index: number) => {
        const isSelected = checkIsSelected(opt.name)
        const color = getButtonColor(isSelected)

        return (
          <button
            key={`opt-${index}`}
            type="button"
            onClick={() => handleSelect(opt.name)}
            disabled={disabled}
            className={`
              w-full px-4 py-3 rounded-lg font-bold text-base transition-all
              ${color}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {opt.name}
          </button>
        )
      })}
    </div>
  )
}
